import { useEffect, useRef, useState } from 'preact/hooks';
import type { LockGuardWidget } from '../widgets';
import type { IPointBindingSource } from '../binding';
import { pointSignal } from '../pointSignal';
import { isDegraded } from '../quality';
import { showConfirm } from '../../components/Confirmer';
import './LockGuardRenderer.css';

interface Props {
  widget: LockGuardWidget;
  source: IPointBindingSource;
}

/**
 * LockGuard: two-step arm / disarm control for safety-relevant enables
 * ("Handbetrieb freigeben"). Freigeben opens a confirmation dialog with the
 * concept's `confirmationCopy` plus the timeout note; on confirm the arm
 * value is written and a countdown of `timeoutSeconds` starts. During the
 * countdown a red banner is shown and the operator can either let the timer
 * elapse (auto-writes disarmValue) or press "Sperren" to disarm immediately.
 *
 * The countdown is implemented with a `useRef`ed interval id so cleanup on
 * unmount / re-run stays deterministic, plus a `secondsLeft` `useState` for
 * the reactive re-render every tick.
 */
export function LockGuardRenderer({ widget, source }: Props) {
  const { value, release } = pointSignal(source, widget.feedbackPoint);
  useEffect(() => release, [release]);

  const [pending, setPending] = useState<{ target: unknown; state: 'sending' | 'rejected' } | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const timerRef = useRef<number | null>(null);

  const current = value.value;
  const stale = isDegraded(current);
  const armed = valuesEqual(current?.value, widget.armValue);
  const disarmed = valuesEqual(current?.value, widget.disarmValue);

  const armLabel = widget.armLabel ?? 'Freigeben';
  const disarmLabel = widget.disarmLabel ?? 'Sperren';
  const timeoutSeconds = widget.timeoutSeconds ?? 60;

  useEffect(() => () => stopCountdown(), []);

  // Feedback flipped back to disarm on its own (e.g. controller enforced the
  // limit before the client did) — cancel the countdown to avoid a double
  // disarm-write.
  useEffect(() => {
    if (disarmed && secondsLeft !== null) {
      stopCountdown();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disarmed]);

  function stopCountdown() {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setSecondsLeft(null);
  }

  function startCountdown() {
    stopCountdown();
    setSecondsLeft(timeoutSeconds);
    timerRef.current = window.setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev === null) return null;
        if (prev <= 1) {
          if (timerRef.current !== null) {
            window.clearInterval(timerRef.current);
            timerRef.current = null;
          }
          // Fire the auto-disarm outside the setState callback so React state
          // updates from the write path don't race with this tick's render.
          window.setTimeout(() => void commit(widget.disarmValue, 'auto'), 0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  async function commit(target: unknown, mode: 'arm' | 'disarm' | 'auto') {
    setPending({ target, state: 'sending' });
    const ac = new AbortController();
    try {
      for await (const ack of source.write(widget.commandPoint, target, ac.signal)) {
        if (ack.state === 'confirmed') {
          setPending(null);
          if (mode === 'arm') startCountdown();
          else stopCountdown();
          return;
        }
        if (ack.state === 'rejected') {
          setPending({ target, state: 'rejected' });
          if (mode === 'arm') stopCountdown();
          return;
        }
      }
      setPending(null);
    } catch {
      setPending(null);
    }
  }

  async function onArm() {
    if (pending?.state === 'sending') return;
    const ok = await showConfirm({
      title: armLabel,
      message: (widget.confirmationCopy ?? 'Kritische Freigabe. Wirklich fortsetzen?')
        + `\n\nTimeout: ${timeoutSeconds} Sekunden`,
      confirmLabel: armLabel,
      cancelLabel: 'Abbrechen',
      danger: true,
    });
    if (!ok) return;
    await commit(widget.armValue, 'arm');
  }

  async function onDisarm() {
    if (pending?.state === 'sending') return;
    await commit(widget.disarmValue, 'disarm');
  }

  const canWrite = pending?.state !== 'sending';
  const inCountdown = secondsLeft !== null;

  return (
    <div class={`hv-lockguard${stale ? ' hv-lockguard--stale' : ''}${pending ? ` hv-lockguard--${pending.state}` : ''}${inCountdown ? ' hv-lockguard--armed' : ''}`}>
      {widget.label && <div class="hv-lockguard__label">{widget.label}</div>}
      {inCountdown && (
        <div class="hv-lockguard__banner" role="alert">
          In {secondsLeft} Sekunden wird automatisch gesperrt
        </div>
      )}
      <div class="hv-lockguard__row" role="group">
        <button
          type="button"
          class={`hv-lockguard__side${disarmed ? ' hv-lockguard__side--active' : ''}`}
          onClick={onDisarm}
          disabled={!canWrite}
          aria-pressed={disarmed}
        >
          {disarmLabel}
        </button>
        <button
          type="button"
          class={`hv-lockguard__side hv-lockguard__side--arm${armed ? ' hv-lockguard__side--active' : ''}`}
          onClick={onArm}
          disabled={!canWrite || inCountdown}
          aria-pressed={armed}
        >
          {armLabel}
        </button>
      </div>
      {pending?.state === 'sending' && (
        <div class="hv-lockguard__status" role="status">… gesendet, warte auf Regler</div>
      )}
      {pending?.state === 'rejected' && (
        <div class="hv-lockguard__status hv-lockguard__status--rejected" role="status">
          Regler hat den Wert verworfen
        </div>
      )}
    </div>
  );
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || a === undefined || b === null || b === undefined) return false;
  if (typeof a === 'number' && typeof b === 'number') return a === b;
  return String(a) === String(b);
}
